import React, { useState, useEffect, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { View, Image, TouchableOpacity, Modal } from 'react-native';
import { compose } from 'redux';

import { CreatorContainer } from '../../containers';

import { Picker, Text, TextInput } from '../../components';
import { Card, PrimaryButton, SecondaryButton, StatusBadge } from '../../design/components';
import { useUserContext } from '../../../common/utils/context';
import useThemedStyles from '../../hooks/useThemedStyles';
import SwitchWithLabel from '../../components/switch/SwitchWithLabel';
import MachineSelector from '../../components/selector/MachineSelector';
import { TICKET_TYPE, TICKET_TYPE_NAME, ROLE_PERMISSIONS, TICKET_AI_SUGGESTION_STATUS } from '../../../common/constants';
import withMediaPicker from '../../hocs/withMediaPicker';
import { ImageViewer } from '../../components/image';
import { GENERATE_TICKET_AI_SUGGESTION, ACK_TICKET_AI_SUGGESTION } from '../../../common/graphql/ticket';
import Toast from 'react-native-root-toast';

const PAR_LEVEL_DESCRIPTION_HINT = 'Name the affected product and slot number';

const TicketCreator = ({
	values,
	onChange,
	changeFormValue,
	onSubmit,
	submitting,
	machineId,
	docketId,
	captureMedia,
	capturedMediaLink,
	processingMedia,
	disableActions = false,
}) => {
	const { isAVC, hasPermission } = useUserContext();
	const styles = useThemedStyles(createStyles);
	const [modalImageIndex, setModalImageIndex] = useState(null);
	const [aiSuggestion, setAiSuggestion] = useState(null);
	const [aiError, setAiError] = useState(null);
	const [generateSuggestion, { loading: generatingSuggestion }] = useMutation(GENERATE_TICKET_AI_SUGGESTION);
	const [ackSuggestionMutation] = useMutation(ACK_TICKET_AI_SUGGESTION);

	const canSubmitForm = values.description && (!isAVC || values.type);
	const captureTicketMedia = useCallback(() => {
		if (disableActions) {
			Toast.show('Reconnect to continue training');
			return;
		}
		captureMedia();
	}, [captureMedia, disableActions]);

	// Handle captured media link
	useEffect(() => {
		if (capturedMediaLink) {
			const currentImageUrls = values.imageUrls || [];
			changeFormValue({ imageUrls: [...currentImageUrls, capturedMediaLink] });
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [capturedMediaLink]);

	const machineIdForSuggestion = machineId || values.machine;

	const acknowledgeSuggestion = useCallback(async (suggestion, status, appliedFields = []) => {
		if (!suggestion?.id) {
			return;
		}

		try {
			await ackSuggestionMutation({
				variables: {
					id: suggestion.id,
					input: {
						status,
						appliedFields,
					},
				},
			});
		} catch (ackError) {
			console.log('ackTicketAISuggestion error', ackError);
		}
	}, [ackSuggestionMutation]);

	const handleGenerateSuggestion = useCallback(async () => {
		if (!values.description?.trim()) {
			setAiError('Add a short description first');
			return;
		}

		setAiError(null);

		if (aiSuggestion) {
			await acknowledgeSuggestion(aiSuggestion, TICKET_AI_SUGGESTION_STATUS.DISMISSED);
			setAiSuggestion(null);
		}

		try {
			const { data } = await generateSuggestion({
				variables: {
					input: {
						description: values.description,
						machineId: machineIdForSuggestion,
						docketId: docketId || values.docketId,
						source: 'native',
					},
				},
			});

			setAiSuggestion(data?.generateTicketAISuggestion?.suggestion || null);
		} catch (error) {
			setAiError(error?.message || 'Unable to contact AI helper');
		}
	}, [values.description, machineIdForSuggestion, docketId, values.docketId, aiSuggestion, generateSuggestion, acknowledgeSuggestion]);

	const applyAISuggestion = useCallback(async (fields) => {
		if (!aiSuggestion) {
			return;
		}

		if (fields.includes('description') && aiSuggestion.suggestedDescription) {
			changeFormValue({ description: aiSuggestion.suggestedDescription });
		}

		if (fields.includes('type') && aiSuggestion.suggestedType?.id) {
			changeFormValue({ type: aiSuggestion.suggestedType.id });
		}

		await acknowledgeSuggestion(aiSuggestion, TICKET_AI_SUGGESTION_STATUS.APPLIED, fields);
		setAiSuggestion(null);
	}, [aiSuggestion, changeFormValue, acknowledgeSuggestion]);

	const dismissAISuggestion = useCallback(async () => {
		if (!aiSuggestion) {
			return;
		}

		await acknowledgeSuggestion(aiSuggestion, TICKET_AI_SUGGESTION_STATUS.DISMISSED);
		setAiSuggestion(null);
	}, [aiSuggestion, acknowledgeSuggestion]);

	return (
		<View style={styles.form}>
			<View style={styles.field}>
				<Text style={styles.label}>Type</Text>
				<Picker
					selectedValue={values.type}
					onValueChange={value => changeFormValue({ type: Number(value) })}
					options={Object.values(TICKET_TYPE).filter(i => i !== TICKET_TYPE.AUTOMATICALLY_GENERATED).map(item => ({ label: TICKET_TYPE_NAME[item], value: item }))}
					emptyValueLabel="Select type"
					width="100%"
					accessibilityLabel="Type"
				/>
			</View>
			<View style={styles.field}>
				<Text style={styles.label}>Description</Text>
				<TextInput
					multiline={true}
					numberOfLines={4}
					style={styles.multilineInput}
					value={values.description}
					onChange={onChange('description')}
					placeholder={values.type === TICKET_TYPE.PAR_LEVEL_CAPACITY_INCORRECT ? PAR_LEVEL_DESCRIPTION_HINT : undefined}
				/>
			</View>
			{
				isAVC && (
					<View style={styles.field}>
						<SecondaryButton
							label={aiSuggestion ? 'Regenerate with AI' : 'Improve with AI'}
							icon="zap"
							variant="tonal"
							onPress={handleGenerateSuggestion}
							loading={generatingSuggestion}
							disabled={!values.description}
						/>
						{
							aiError && (
								<Text style={styles.aiError}>{aiError}</Text>
							)
						}
						{
							aiSuggestion && (
								<Card tone="raised" style={styles.aiCard}>
									<View style={styles.aiCardHeader}>
										<View style={styles.aiCardHeading}>
											<Text style={styles.aiCardTitle}>AI suggestion</Text>
											<Text style={styles.aiCardSubtitle}>Review these fields before applying.</Text>
										</View>
										<SecondaryButton
											label="Dismiss"
											variant="tonal"
											onPress={dismissAISuggestion}
										/>
									</View>
									<Text style={styles.aiDescription}>{aiSuggestion.suggestedDescription}</Text>
									<View style={styles.aiMetaRow}>
										<View style={styles.aiMetaBlock}>
											<Text style={styles.aiMetaLabel}>Suggested type</Text>
											{
												aiSuggestion.suggestedType?.label ? (
													<>
														<Text style={styles.aiMetaValue}>{aiSuggestion.suggestedType.label}</Text>
														<Text style={styles.aiMetaCategory}>{aiSuggestion.suggestedType.category}</Text>
													</>
												) : (
													<Text style={styles.aiMetaValue}>Need more info</Text>
												)
											}
										</View>
										{
											typeof aiSuggestion.confidence === 'number' && (
												<View style={styles.aiMetaBlock}>
													<Text style={styles.aiMetaLabel}>Confidence</Text>
													<StatusBadge label={`${Math.round(aiSuggestion.confidence * 100)}%`} tone="primary" size="small" />
												</View>
											)
										}
									</View>
									<View style={styles.aiActions}>
										<PrimaryButton
											label="Apply text & type"
											size="compact"
											onPress={() => applyAISuggestion(['description', 'type'])}
											disabled={!aiSuggestion.suggestedType?.id}
										/>
										<SecondaryButton
											label="Apply text only"
											onPress={() => applyAISuggestion(['description'])}
										/>
									</View>
								</Card>
							)
						}
					</View>
				)
			}
			{
				!machineId && (
					<MachineSelector
						onChange={m => changeFormValue({ machine: m.id })}
					/>
				)
			}
			{
				Boolean(values.docketId) && (
					<View style={styles.switchCard}>
						<SwitchWithLabel
							onValueChange={() => changeFormValue({ addToCurrentService: !values.addToCurrentService })}
							value={values.addToCurrentService}
							label="Add to the current service"
						/>
					</View>
				)
			}

			{/* Photo Section */}
			<View style={styles.field}>
				<Text style={styles.label}>Photos</Text>
				{
					values.imageUrls && values.imageUrls.length > 0 && (
						<View style={styles.photoRow}>
							{
								values.imageUrls.map((imageUrl, index) => (
									<TouchableOpacity
										key={index}
										onPress={() => setModalImageIndex(index)}
										accessibilityRole="imagebutton"
										accessibilityLabel={`Ticket photo ${index + 1}`}
									>
										<Image
											style={styles.photo}
											source={{ uri: imageUrl }}
										/>
									</TouchableOpacity>
								))
							}
						</View>
					)
				}
				<SecondaryButton
					label="Add photo"
					icon="camera"
					onPress={captureTicketMedia}
					disabled={disableActions || !hasPermission(ROLE_PERMISSIONS.ADD_EDIT_TICKET_PHOTOS) || processingMedia}
					loading={processingMedia}
				/>
			</View>
			<PrimaryButton
				label="Create ticket"
				onPress={onSubmit}
				loading={submitting}
				disabled={disableActions || !canSubmitForm}
			/>

			{/* Photo Modal Viewer */}
			{
				values.imageUrls && (
					<Modal
						visible={modalImageIndex !== null}
						transparent={true}
						onRequestClose={() => setModalImageIndex(null)}
					>
						<ImageViewer
							close={() => setModalImageIndex(null)}
							index={modalImageIndex}
							imageUrls={values.imageUrls.map(url => ({ url }))}
						/>
					</Modal>
				)
			}
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	form: {
		gap: space.lg,
	},
	field: {
		gap: 6,
	},
	label: {
		...type.fieldLabel,
		color: colors.text,
	},
	multilineInput: {
		height: 100,
	},
	aiError: {
		...type.caption,
		color: colors.danger,
	},
	aiCard: {
		padding: 14,
		gap: space.sm,
		marginTop: space.xs,
	},
	aiCardHeader: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: space.sm,
	},
	aiCardHeading: {
		flex: 1,
	},
	aiCardTitle: {
		...type.bodySmallSemibold,
		color: colors.text,
	},
	aiCardSubtitle: {
		...type.caption,
		color: colors.textSecondary,
		marginTop: 2,
	},
	aiDescription: {
		...type.bodySmall,
		color: colors.text,
	},
	aiMetaRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.lg,
	},
	aiMetaBlock: {
		gap: 2,
	},
	aiMetaLabel: {
		...type.overline,
		color: colors.textSecondary,
	},
	aiMetaValue: {
		...type.bodySmallSemibold,
		color: colors.text,
	},
	aiMetaCategory: {
		...type.caption,
		color: colors.textSecondary,
	},
	aiActions: {
		gap: space.sm,
	},
	switchCard: {
		paddingHorizontal: 14,
		borderRadius: radius.card,
		backgroundColor: colors.card,
	},
	photoRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
		marginBottom: space.xs,
	},
	photo: {
		height: 72,
		width: 72,
		borderRadius: radius.control,
		backgroundColor: colors.chip,
		resizeMode: 'cover',
	},
});

const WrappedTicketCreator = compose(CreatorContainer, withMediaPicker)(TicketCreator);

const TicketCreatorWithPicker = (props) => {
	// Scope multi-select to ticket photo uploads.
	return (
		<WrappedTicketCreator
			allowMultipleSelection={true}
			allowMultipleCapture={true}
			{...props}
		/>
	);
};

export default TicketCreatorWithPicker;
